// import node module libraries
import React, { Fragment, useEffect, useMemo, useState } from "react";
import StatRightIcon from "components/StatRightIcon";
import { ReactComponent as MoneyCardSvg } from "assets/svgs/money-card.svg";
import { Link, useHistory } from "react-router-dom";
import { Col, Row, Spinner } from "react-bootstrap";

import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import CustomButton from "components/CustomButton";
import {
  useBulkUpdateOrderTransactionsStatusMutation,
  useGetTransactionsQuery,
  useLazyGetTransactionsQuery,
} from "redux/features/user/userApi";
import { useSelector } from "react-redux";
import { isSubstringInArray } from "helper/formatArray";
import moment from "moment";
import { ChangeTransactionStatus } from "pages/transactions/components/ChangeTransactionStatus";
import DataTable from "components/DataTable";
import { capitalize, dateString, formatCurrency } from "helper/formatText";
import { TextSearchFilter } from "components/advance-table/filters";
import { convertJsonStringToObject } from "helper/formatObject";
import { pageOffset, listParams, useCount, useTablePage } from "hooks/useServerPagination";
import { LOCAL_COUNTRY } from "config";

const GlobalOrdersTransactions = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { countries } = useSelector((state) => state.userAuth);
  const [refresh, setRefresh] = useState(false);

  const [getTransactions, { data: response, isLoading }] = useLazyGetTransactionsQuery();
  const list = useMemo(() => (response?.data || []).map((x) => convertJsonStringToObject(JSON.parse(JSON.stringify(x)))), [response]);
  const [tableState, onPageChange] = useTablePage();

  const filters = { type: "order_payment", "country[ne]": LOCAL_COUNTRY };
  const pending = useCount(useGetTransactionsQuery, { ...filters, status: "pending" });
  const settled = useCount(useGetTransactionsQuery, { ...filters, status: "settled" });
  const refunded = useCount(useGetTransactionsQuery, { ...filters, status: "refunded" });
  const stats = { pending, settled, refunded };

  const fetchList = async () => {
    const res = await getTransactions({ ...filters, ...listParams(tableState, columns, "-updatedAt") });

    if (res?.error) {
      toast.error(res?.error?.data?.message || "Something went wrong", {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
  };

  useEffect(() => {
    if (tableState) fetchList();
  }, [refresh, tableState]);

  const [statusModal, setStatusModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkUpdateTransactionsStatus, { isLoading: isBulkUpdating }] = useBulkUpdateOrderTransactionsStatusMutation();
  const updateTransactionStatus = async (status) => {
    if (selectedRows?.filter((x) => x?.type == "order_payment")?.length !== selectedRows?.length) {
      toast.error("Bulk updating different transaction types is not allowed", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    const commonStatus = selectedRows?.filter((x) => x?.status == selectedRows[0]?.status);
    if (commonStatus?.length !== selectedRows?.length) {
      toast.error("Bulk updating transactions with different statuses is not allowed", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    const res = await bulkUpdateTransactionsStatus({
      ids: selectedRows?.map((x) => {
        return x?.id;
      }),
      value: status,
    });
    if (res?.data?.status === "success") {
      toast.success(res?.data?.message || "Bulk update was successful", {
        position: toast.POSITION.TOP_RIGHT,
      });
      setStatusModal(false);
      setRefresh(!refresh);
    } else {
      toast.error(res?.error?.data?.message || "Something went wrong", {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
  };

  const columns = useMemo(
    () => [
      // { accessor: "sn", Header: "S/N" },
      //   { accessor: "id", Header: "ID" },
      {
        accessor: "name",
        sortField: "type",
        Header: "Name",
        // Filter: TextSearchFilter,
        // filter: "rankedMatchSorter",
        Cell: ({ value, row }) => {
          return (
            <Link className="text-inherit " to={{ pathname: `/transactions/${row?.original?.id}`, state: row?.original }}>
              <h5 className="text-primary-hover mb-0">{value}</h5>
            </Link>
          );
        },
      },
      {
        accessor: "status",
        Header: "Status",
        Cell: ({ value, row }) => {
          return capitalize(value);
        },
        Filter: TextSearchFilter,
        filter: "rankedMatchSorter",
      },
      {
        accessor: "Amount",
        sortField: "amount",
        Header: "Amount",
        // Filter: TextSearchFilter,
        // filter: "rankedMatchSorter",
      },
      {
        accessor: "orderId",
        disableSortBy: true,
        Header: "Related Order ID",
        Cell: ({ value, row }) => {
          return (
            <Link className="text-inherit " to={{ pathname: `/orders/${row?.original?.order_id?.id}`, state: row?.original?.order_id }}>
              <h5 className="text-primary-hover mb-0">{value}</h5>
            </Link>
          );
        },
      },
      {
        accessor: "payment_status",
        disableSortBy: true,
        Header: "Payment status",
      },
      {
        accessor: "market",
        disableSortBy: true,
        Header: "Market",
        // Filter: TextSearchFilter,
        // filter: "rankedMatchSorter",
      },
      {
        accessor: "userName",
        disableSortBy: true,
        Header: "Sender/Recipient",
        // Filter: TextSearchFilter,
        // filter: "rankedMatchSorter",
      },
      {
        accessor: "userPhone",
        disableSortBy: true,
        Header: "Phone number",
      },
      {
        accessor: "user_model_name",
        Header: "User Type",
      },
      {
        accessor: "entry",
        Header: "Entry",
        Cell: ({ value, row }) => {
          return capitalize(value);
        },
      },
      {
        accessor: "paymentGateway",
        sortField: "payment_gateway",
        Header: "Payment method",
      },
      {
        accessor: "reference",
        Header: "Reference",
        Filter: TextSearchFilter,
        filter: "rankedMatchSorter",
      },
      {
        accessor: "created_At",
        sortField: "createdAt",
        Header: "Date created",
      },
      {
        accessor: "updated_At",
        sortField: "updatedAt",
        Header: "Last updated",
      },
    ],
    [],
  );

  const data = useMemo(() => {
    let filtered = list || [];
    filtered = filtered?.map((x, index) => {
      const currency = countries?.find((y) => isSubstringInArray(y?.name, [x?.country || LOCAL_COUNTRY]))?.currency;

      return {
        ...x,
        sn: pageOffset(response) + index + 1,
        name: capitalize(x?.type?.replace("_", " ")),
        userName: x?.user_id?.firstName + " " + x?.user_id?.lastName,
        userEmail: x?.user_id?.email,
        userPhone: x?.user_id?.phoneNumber?.toString(),
        market: x?.order_id?.market?.name,
        orderId: x?.order_id?.order_id,
        Amount: `${formatCurrency(x?.amount, 2, x?.type == "order_payment" ? currency?.symbol : undefined)}`,
        paymentGateway: capitalize(x?.payment_gateway?.replace("_", " ")),
        payment_status: capitalize(x?.order_id?.payment_status?.replace("_", " ")),
        created_At: moment(dateString(x?.createdAt)).format("ddd MMM DD, yyyy"),
        updated_At: moment(dateString(x?.updatedAt)).format("ddd MMM DD, yyyy"),
      };
    });
    return filtered;
  }, [list, response]);

  useEffect(() => {
    setStatusModal(false);
  }, [selectedRows]);

  const toggleStatusModal = () => {
    setStatusModal(!statusModal);
  };

  const handleTransactionStatusChange = async (status) => {
    await updateTransactionStatus(status);
  };

  if (isLoading) {
    return (
      <Row className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner style={{ color: "#e0bf00" }} />
      </Row>
    );
  }

  return (
    <Fragment>
      <div className="overflow-hidden justify-content-space-around">
        <Row>
          <Col xl={4} lg={4} md={6} sm={12}>
            <StatRightIcon
              title="Pending Payments"
              value={stats.pending}
              iconComponent={<MoneyCardSvg />}
              iconColorVariant="primary"
              classValue="mb-4"
            />
          </Col>
          <Col xl={4} lg={4} md={6} sm={12}>
            <StatRightIcon
              title="Settled Payments"
              value={stats.settled}
              iconComponent={<MoneyCardSvg />}
              iconColorVariant="primary"
              classValue="mb-4"
            />
          </Col>
          <Col xl={4} lg={4} md={6} sm={12}>
            <StatRightIcon
              title="Refunded Payments"
              value={stats.refunded}
              iconComponent={<MoneyCardSvg />}
              iconColorVariant="primary"
              classValue="mb-4"
            />
          </Col>
        </Row>
      </div>
      <Row>
        <DataTable
          serverPagination={response}
          onPageChange={onPageChange}
          items={list}
          title="Global Orders Transactions"
          detailsRedirect="/transactions/:id"
          columns={columns}
          data={data}
          excelFileName={"FD_Global_Order_Transactions"}
          excelSheetName={"Global_Order_Transactions"}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          externalActionButton={
            selectedRows?.length > 0 && <CustomButton variant="primary" type="button" text="Update Status" onClick={toggleStatusModal} />
          }
        />
      </Row>
      <ChangeTransactionStatus
        isBulk={true}
        initial={selectedRows[0]?.status}
        show={statusModal && selectedRows?.length > 0}
        close={toggleStatusModal}
        onConfirm={handleTransactionStatusChange}
        isLoading={isBulkUpdating}
        removedOptions={["rejected", "cancelled", "canceled", "approved"]}
      />
    </Fragment>
  );
};

export default GlobalOrdersTransactions;
